import { apiClient } from "../client";

/**
 * Templates used to seed a new campus with its welcome space, event and idea.
 * A single global row — no ids in any of these routes.
 */

export interface DefaultContentWalky {
  first_name: string;
  avatar_url: string;
  profile_bio: string;
}

export interface DefaultContentSpace {
  title: string;
  about: string;
  logo_url: string;
  cover_image_url: string;
  member_count_range: string;
}

export interface DefaultContentEvent {
  enabled: boolean;
  name: string;
  description: string;
  /** 0=Sun..6=Sat */
  day_of_week: number;
  /** Local start hour, 0-23 */
  hour: number;
  duration_minutes: number;
  slots: number;
  event_type: "outdoor" | "indoor";
  image_url: string;
}

export interface DefaultContentIdea {
  enabled: boolean;
  title: string;
  description: string;
  looking_for: string;
}

export interface DefaultContentSettings {
  _id: string;
  key: string;
  enabled: boolean;
  walky: DefaultContentWalky;
  space: DefaultContentSpace;
  event: DefaultContentEvent;
  idea: DefaultContentIdea;
  updatedAt: string;
}

export interface DefaultContentSettingsUpdate {
  enabled?: boolean;
  walky?: Partial<DefaultContentWalky>;
  space?: Partial<DefaultContentSpace>;
  event?: Partial<DefaultContentEvent>;
  idea?: Partial<DefaultContentIdea>;
}

export interface DefaultContentPreview {
  vars: { campus: string; school: string };
  space: { title: string; about: string };
  event: { enabled: boolean; name: string; description: string };
  idea: {
    enabled: boolean;
    title: string;
    description: string;
    looking_for: string;
  };
}

export const defaultContentAPI = {
  getSettings: async (): Promise<DefaultContentSettings> => {
    const response = await apiClient.get("/api/admin/default-content");
    return response.data.data;
  },

  updateSettings: async (
    update: DefaultContentSettingsUpdate,
  ): Promise<DefaultContentSettings> => {
    const response = await apiClient.put("/api/admin/default-content", update);
    return response.data.data;
  },

  resetSettings: async (): Promise<DefaultContentSettings> => {
    const response = await apiClient.post("/api/admin/default-content/reset");
    return response.data.data;
  },

  /**
   * Uploads an image and stores its URL in the matching setting, so an admin
   * can pick a file instead of finding somewhere to host one.
   */
  uploadImage: async (
    slot: "walky-avatar" | "space-logo" | "space-cover" | "event-cover",
    file: File,
  ): Promise<{ url: string; data: DefaultContentSettings }> => {
    const form = new FormData();
    form.append("image", file);
    const response = await apiClient.post(
      `/api/admin/default-content/upload/${slot}`,
      form,
      // Let the browser set the multipart boundary; the client's default JSON
      // content-type would make the backend see no file.
      { headers: { "Content-Type": undefined } },
    );
    return { url: response.data.url, data: response.data.data };
  },

  /**
   * Pushes the current settings onto campuses that were seeded earlier, so an
   * edit here reaches everyone rather than only future campuses.
   */
  reapply: async (): Promise<{
    spaces: number;
    events: number;
    ideas: number;
    accounts: number;
  }> => {
    const response = await apiClient.post("/api/admin/default-content/reapply");
    return response.data.data;
  },

  /** Renders the templates without creating anything, for the live preview. */
  preview: async (
    campus: string,
    school: string,
  ): Promise<DefaultContentPreview> => {
    const response = await apiClient.get("/api/admin/default-content/preview", {
      params: { campus, school },
    });
    return response.data.data;
  },
};
